import { HillHudTracker, loadHillBests, saveHillBests, SCORE_KEY, type HillHud } from "./hill";
import { COURSE, TUTORIAL_COURSE } from "./hill-course";
import { placeRoad } from "./hill-ramps";
import { HillTutorial, markHillTutorialSeen, type HillLessonView } from "./hill-tutorial";
import type { Level } from "./level";
import type { Skater } from "./skater";
import type { SkaterEvent } from "./sim/state";
import { storage } from "./storage";

// Bomb the Hill's run state (#218), three.js-free, beside `ChallengeBoard`:
// the best distance and best total down each hill (saved), the HUD's
// tracker for the run on one, and the tutorial (#201) while it runs, with
// what it reads of the rider. Game holds one for the session and keeps the
// road's meshes, the camera and the UI's thin calls into it.

export class HillSession {
  /** The best distance down each hill (#193), by map id. */
  private bests = loadHillBests(storage());
  /** ...and the best total (#205). */
  private bestScores = loadHillBests(storage(), SCORE_KEY);
  private tracker = new HillHudTracker();
  /** The tutorial (#201), while it runs: on the hill only, dropped on any other map. */
  private tutorial: HillTutorial | null = null;

  /** A map was loaded: the tutorial only carries on on a hill. */
  mapLoaded(level: Level): void {
    if (!level.hill) this.tutorial = null;
  }

  /** Starts the tutorial on the next run down the hill (`seed` for its road), after loading the hill. */
  startTutorial(level: Level, seed: () => number, clock: number): void {
    const road = level.hill?.road;
    if (!road) return;
    this.tutorial = new HillTutorial();
    // The tutorial's own road, from this run on until it's done.
    road.tuning = TUTORIAL_COURSE;
    road.reset(seed(), clock);
    placeRoad(level);
  }

  /** Skips the rest of the tutorial: it isn't offered by itself again. */
  skipTutorial(level: Level): void {
    if (!this.tutorial) return;
    this.tutorial = null;
    markHillTutorialSeen(storage());
    // The next run's road is a run's own again (this one carries on as it was laid out).
    const road = level.hill?.road;
    if (road) road.tuning = COURSE;
  }

  /**
   * Steps the tutorial `ms` along against the rider, ending it once its last
   * lesson is read. Returns whether it moved on a lesson (a cue to play).
   */
  stepTutorial(sk: Skater, level: Level, ms: number): boolean {
    const view = this.tutorial ? lessonView(sk, level) : null;
    if (!this.tutorial || !view || !this.tutorial.step(view, ms)) return false;
    if (this.tutorial.done) this.skipTutorial(level);
    return true;
  }

  /** A run down the hill ended (the `"hill"` event): the HUD's card says how far, and the bests are kept. */
  ended(mapId: string, e: Extract<SkaterEvent, { type: "hill" }>): void {
    const best = this.tracker.end(e.end, e.distance, this.bests[mapId] ?? 0, e.hit ?? null, e.score, this.bestScores[mapId] ?? 0);
    if (best.distance) {
      this.bests = { ...this.bests, [mapId]: e.distance };
      saveHillBests(storage(), this.bests);
    }
    if (best.score) {
      this.bestScores = { ...this.bestScores, [mapId]: e.score.total };
      saveHillBests(storage(), this.bestScores, SCORE_KEY);
    }
  }

  /** The HUD's `hill` part: null off a hill. */
  hud(sk: Skater, mapId: string): HillHud | null {
    return this.tracker.hud(
      sk,
      this.bests[mapId] ?? 0,
      this.tutorial && sk.hill ? this.tutorial.hud({ picking: sk.hill.picking, ended: sk.hill.end !== null }) : null,
      this.bestScores[mapId] ?? 0,
    );
  }
}

/** What the hill's tutorial (#201) reads of the rider. */
function lessonView(s: Skater, level: Level): HillLessonView | null {
  const h = s.hill;
  if (!h) return null;
  const road = level.hill?.road;
  const offset = road?.offset ?? 0;
  // The next row of parked vehicles below the rider, in course z so it holds still as the road moves back up.
  let nextRow: number | null = null;
  for (const o of road?.course.obstacles ?? []) {
    if ((o.kind === "car" || o.kind === "van" || o.kind === "bus") && o.z > s.z + 2 && (nextRow === null || o.z + offset < nextRow)) nextRow = o.z + offset;
  }
  // The far side of the next crosswalk and side street still ahead of the rider.
  const farSide = (list: readonly { z: number; hl: number }[]): number | null => {
    const ahead = list.filter((x) => x.z - x.hl > s.z + 2).map((x) => x.z + x.hl + offset);
    return ahead.length ? Math.min(...ahead) : null;
  };
  const course = road?.course;
  return {
    picking: h.picking,
    ended: h.end !== null,
    carve: h.carve,
    wobble: h.wobble,
    air: s.mode === "air",
    z: s.z + offset,
    nextRow,
    nextCrossing: farSide(course?.crossings ?? []),
    nextJunction: farSide(course?.junctions ?? []),
  };
}
